import { apiCap } from '@gik/api';
import { useTaxonomies } from '@gik/api/give/taxonomies';
import { usePostsByTaxonomy } from '@gik/api/posts/postsByTaxonomy';
import { ArticlesCarousel } from '@gik/blog/components/ArticlesCarousel/ArticlesCarousel';
import type Article from '@gik/blog/models/Article';
import WordpressConverter from '@gik/blog/utils/WordpressConverter';
import { FetchProvider } from '@gik/core/components/DynamicComponent/FetchProvider';
import { InViewport } from '@gik/core/components/InViewport';
import { withTitle } from '@gik/core/hocs/withTitle';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';
import { useIsMounted } from '@gik/core/hooks/hooks/useIsMounted';
import type { Product } from '@gik/core/models/gik/Product';
import type WordpressPost from '@gik/core/models/wordpress/WordpressPost';
import type { WordpressTaxonomy } from '@gik/core/models/wordpress/WordpressTaxonomy';
import { ErrorPageSection } from '@gik/core/pages/Error/ErrorPageSection';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { CreateClickedLocation } from '@gik/create/enums/CreateAnalyticsEnums';
import { ProductsGridSection } from '@gik/shop/components/ProductsGridSection';
import { TaxonomyPageLatestArticles } from '@gik/shop/components/RelatedArticles/TaxonomyPageLatestArticles';
import { TaxonomyPageRelatedArticles } from '@gik/shop/components/RelatedArticles/TaxonomyPageRelatedArticles';
import { TaxonomyListWithSituations } from '@gik/shop/components/TaxonomyList/TaxonomyListWithSituations';
import { TaxonomyNavWithData } from '@gik/shop/components/TaxonomyNav/TaxonomyNavWithData';
import type { GiveTaxonomyType } from '@gik/shop/types/GiveTaxonomyType';
import { ArticleTile } from '@gik/ui/gik/ArticleTile';
import { PageSection } from '@gik/ui/gik/PageSection';
import { StartInkindCard } from '@gik/ui/gik/StartInkindCard';
import { LoadingSpinner } from '@gik/ui/LoadingSpinner';
import React from 'react';
import { ByGiftTypeSection } from '../../components/ByGiftTypeSection';
import { ByRecipientSection } from '../../components/ByRecipientSection';
import { GivePageLayout } from '../../components/GivePageLayout';

export type TaxonomyPageServerProps = {
  situations: WordpressTaxonomy[];
  recipients: WordpressTaxonomy[];
  categories: WordpressTaxonomy[];
  products: Product[];
  productsTotalPages: number;
  posts: WordpressPost[];
  posts2: WordpressPost[];
};

export type TaxonomyPageProps = {
  taxonomyType: GiveTaxonomyType;
  parentTaxonomy: string;
  childTaxonomy: string;

  fallbackData?: TaxonomyPageServerProps;
} & UIComponent;

const defaultHeaderCopy =
  'Find a meaningful gift to send in any situation. Give InKind’s recommend gifts have been curated to offer comfort for whoever you want to support.';

function TaxonomyPageComp(props: TaxonomyPageProps): React.ReactElement {
  const bem = useBemCN('taxonomy-page');
  const isMdDown = useBreakpoint(Breakpoint.MD_DOWN);
  const isMounted = useIsMounted();

  const [currentSlug, setCurrentSlug] = React.useState<string>();

  const { taxonomyType, parentTaxonomy, childTaxonomy } = props;

  const { data: situations, error: situationsError } = useTaxonomies(
    'situation',
    { page: 1, perPage: apiCap },
    {
      fallbackData: props.fallbackData?.situations,
    }
  );

  const { data: recipients, error: recipientsError } = useTaxonomies(
    'recipient',
    { page: 1, perPage: apiCap },
    {
      fallbackData: props.fallbackData?.recipients,
    }
  );

  const { data: categories, error: categoriesError } = useTaxonomies(
    'category',
    { page: 1, perPage: apiCap },
    {
      fallbackData: props.fallbackData?.categories,
    }
  );

  const slug = currentSlug || childTaxonomy || parentTaxonomy;

  function findTaxonomy(
    taxonomyType: GiveTaxonomyType,
    slug: string,
    _situations = situations,
    _recipients = recipients,
    _categories = categories
  ) {
    switch (taxonomyType) {
      case 'situation':
        return _situations?.find(item => item.slug === slug);
      case 'recipient':
        return _recipients?.find(item => item.slug === slug);
      case 'category':
        return _categories?.find(item => item.slug === slug);
    }
  }

  function findTaxonomies(taxonomyType: GiveTaxonomyType) {
    switch (taxonomyType) {
      case 'situation':
        return situations;
      case 'recipient':
        return recipients;
      case 'category':
        return categories;
    }
  }

  const taxonomyItem = findTaxonomy(taxonomyType, slug);
  const hasError = situationsError || recipientsError || categoriesError;

  // if (!taxonomy && isBrowser()) {
  //   navigateTo(routes.give);
  //   return null;
  // }
  if (hasError) return <LoadingSpinner />;

  const taxonomyProps = {
    taxonomyType,
    parentSlug: parentTaxonomy,
    childSlug: currentSlug || childTaxonomy,
  };

  function handleNavChange(slug: string) {
    setCurrentSlug(slug);
  }

  return withTitle(
    {
      title: taxonomyItem?.name,
      fallback: findTaxonomy(
        taxonomyType,
        slug,
        props?.fallbackData?.situations,
        props?.fallbackData?.recipients,
        props?.fallbackData?.categories
      )?.name,
    },
    <GivePageLayout {...bem()} taxonomyItem={taxonomyItem}>
      <PageSection noPad {...bem('section-nav')}>
        <TaxonomyNavWithData
          {...taxonomyProps}
          selected={slug}
          items={findTaxonomies(taxonomyType)}
          onChange={handleNavChange}
        />
      </PageSection>

      <PageSection defaultPadding {...bem('section-lead')}>
        <div {...bem('box-lead')}>
          <div {...bem('box-lead-content')}>
            <p>{taxonomyItem?.acf?.header_copy?.length ? taxonomyItem.acf.header_copy : defaultHeaderCopy}</p>
          </div>
        </div>
      </PageSection>

      {
        <PageSection defaultPadding {...bem('section-taxonomy-results')}>
          <ProductsGridSection
            key={taxonomyItem?.id}
            title={taxonomyItem?.name}
            {...taxonomyProps}
            currentSlug={currentSlug}
            fallbackData={props.fallbackData}
            emphasis={
              taxonomyType === 'situation' &&
              taxonomyItem && {
                title: `Learn About Ways to Help`,
                node: (
                  <FetchProvider<WordpressPost[], Article[]>
                    fetch={function useFetch() {
                      return usePostsByTaxonomy(
                        {
                          taxonomyItemSlug: taxonomyItem?.slug,
                          taxonomySlug: 'situation',
                          perPage: 4,
                        },
                        { fallbackData: props.fallbackData?.posts }
                      );
                    }}
                    transform={data =>
                      data.map(WordpressConverter.convertPost).filter(WordpressConverter.filterOutPreviewContent)
                    }
                    loadingComponent={<ArticleTile skeleton />}
                  >
                    {({ transformed }) => <ArticlesCarousel articles={transformed} />}
                  </FetchProvider>
                ),
              }
            }
          />

          {taxonomyType === 'situation' && taxonomyItem && isMdDown && isMounted() && (
            <>
              <span {...bem('taxonomy-results-title')}>Learn About Ways to Help</span>

              <FetchProvider<WordpressPost[], Article[]>
                fetch={function useFetch() {
                  return usePostsByTaxonomy(
                    {
                      taxonomyItemSlug: taxonomyItem?.slug,
                      taxonomySlug: 'situation',
                      perPage: 4,
                    },
                    { fallbackData: props.fallbackData?.posts }
                  );
                }}
                transform={data =>
                  data.map(WordpressConverter.convertPost).filter(WordpressConverter.filterOutPreviewContent)
                }
                loadingComponent={<ArticleTile layout={'row'} skeleton />}
              >
                {({ transformed }) => <ArticlesCarousel articles={transformed} />}
              </FetchProvider>
            </>
          )}
        </PageSection>
      }

      <InViewport height={175}>
        <StartInkindCard trackingId={CreateClickedLocation.PAGE_FEATURES} />
      </InViewport>

      <PageSection noPadTop {...bem('related-articles')}>
        {taxonomyType !== 'category' ? (
          <TaxonomyPageRelatedArticles
            taxonomyItemSlug={taxonomyItem?.slug}
            taxonomySlug={taxonomyType}
            fallbackData={taxonomyType === 'situation' ? props.fallbackData?.posts2 : props.fallbackData?.posts}
            title={taxonomyItem?.name || ''}
            page={taxonomyType === 'situation' ? 2 : 1}
          />
        ) : (
          <TaxonomyPageLatestArticles />
        )}
      </PageSection>

      {/* {taxonomyType !== 'situation' && (
        <DynamicComponent height={786}>
          <BySituationSection
            fallbackData={props.fallbackData?.situations}
            taxonomyType={taxonomyType}
            parentTaxonomy={parentTaxonomy}
          />
        </DynamicComponent>
      )} */}

      <InViewport height={786}>
        <ByRecipientSection fallbackData={props.fallbackData?.recipients} />
      </InViewport>

      <InViewport height={786}>
        <ByGiftTypeSection loadMoreButton fallbackData={props.fallbackData?.categories} />
      </InViewport>

      <PageSection>
        <span className="gik-give-section-title">Browse by Situation</span>
        <TaxonomyListWithSituations selected={slug} items={props.fallbackData?.situations} />
      </PageSection>
    </GivePageLayout>
  );
}

export const TaxonomyPage = withComponentErrorBoundary(TaxonomyPageComp, { ErrorFallback: ErrorPageSection });
